import { useNavigate } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Eye, FileBarChart, FilePlus, Pencil } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { CampaniaPendiente } from '../types'

interface CampaniasPendientesTableProps {
  campanias: CampaniaPendiente[]
  showReporteButton?: boolean
  onCreateReporte?: (campania: CampaniaPendiente) => void
  onEditReporte?: (campania: CampaniaPendiente) => void
}

export function CampaniasPendientesTable({
  campanias,
  showReporteButton = false,
  onCreateReporte,
  onEditReporte,
}: CampaniasPendientesTableProps) {
  const navigate = useNavigate()

  return (
    <div className="rounded-md border border-gray-300 dark:border-gray-700">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
            <TableHead>Campana</TableHead>
            <TableHead>Laboratorio</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Fecha Envio</TableHead>
            <TableHead className="text-center">Dias Pendiente</TableHead>
            <TableHead className="text-center">{showReporteButton ? 'Ultima Edicion' : 'Seguimientos'}</TableHead>
            <TableHead>Ultimo Seguimiento</TableHead>
            <TableHead className="text-right">Accion</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {campanias.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                No hay campanas con reportes pendientes
              </TableCell>
            </TableRow>
          ) : (
            campanias.map((c) => (
              <TableRow
                key={c.id}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-[hsl(220,15%,16%)]"
                onClick={() => navigate(`/seguimiento-reportes/${c.id}`)}
              >
                <TableCell className="font-medium max-w-[250px] truncate">
                  {c.nombre_campania}
                </TableCell>
                <TableCell>{c.marca_principal_nombre || c.marca}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">
                    {c.tipo}
                  </Badge>
                </TableCell>
                <TableCell>
                  {c.fecha_publicacion
                    ? new Date(c.fecha_publicacion + 'T00:00:00').toLocaleDateString('es-EC')
                    : '-'}
                </TableCell>
                <TableCell className="text-center">
                  <span className={`font-semibold ${c.dias_desde_envio > 7 ? 'text-red-500' : c.dias_desde_envio > 3 ? 'text-amber-500' : 'text-green-500'}`}>
                    {c.dias_desde_envio}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  {showReporteButton ? (
                    c.fecha_ultima_edicion
                      ? new Date(c.fecha_ultima_edicion).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : <span className="text-muted-foreground">-</span>
                  ) : (
                    <Badge variant="secondary">{c.total_seguimientos}</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {c.ultimo_seguimiento
                    ? new Date(c.ultimo_seguimiento + 'T00:00:00').toLocaleDateString('es-EC')
                    : <span className="text-muted-foreground">Sin registro</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {/* Botón Crear Reporte (tab Sin Reporte) */}
                    {!showReporteButton && onCreateReporte && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              onCreateReporte(c)
                            }}
                          >
                            <FilePlus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Crear Reporte</TooltipContent>
                      </Tooltip>
                    )}
                    {/* Botón Editar Reporte (tab En Recopilacion) */}
                    {showReporteButton && onEditReporte && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              onEditReporte(c)
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Editar Reporte</TooltipContent>
                      </Tooltip>
                    )}
                    {showReporteButton && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/multichannel/reporte/${c.id}`)
                            }}
                          >
                            <FileBarChart className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Ver Reporte</TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/seguimiento-reportes/${c.id}`)
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Ver Detalle</TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
